import { FormContext } from './formContext';
import { MutableRefObject, ReactNode, useEffect } from 'react';
import {
  DefaultValues,
  FieldValues,
  FormProvider,
  useForm,
  SubmitHandler,
  Resolver,
  UseFormReturn
} from 'react-hook-form';

export interface FormProps<T extends FieldValues> {
  children: ReactNode;
  defaultValues?: DefaultValues<T>;
  onSubmit?: SubmitHandler<T>;
  resetAfterSubmit?: boolean;
  isLoading?: boolean;
  resolver?: Resolver<T>;
  validateMode?: 'onChange' | 'onBlur' | 'onSubmit' | 'onTouched';
  readOnly?: boolean;
  formRef?: MutableRefObject<UseFormReturn<T> | null>;
}

export const Form = <T extends FieldValues>({
  children,
  defaultValues,
  onSubmit,
  resetAfterSubmit = true,
  isLoading = false,
  resolver,
  validateMode = 'onBlur',
  readOnly = false,
  formRef
}: FormProps<T>) => {
  const methods = useForm<T>({ defaultValues, mode: validateMode, resolver });

  useEffect(() => {
    if (!formRef) return;
    formRef.current = methods;
    return () => {
      formRef.current = null;
    };
  }, [formRef, methods]);

  useEffect(() => {
    if (methods.formState.isSubmitSuccessful && resetAfterSubmit) {
      methods.reset(defaultValues);
    }
  }, [defaultValues, methods, resetAfterSubmit]);

  return (
    <FormProvider {...methods}>
      <FormContext.Provider value={{ isLoading, readOnly }}>
        <form onSubmit={methods.handleSubmit(onSubmit ?? (() => {}))}>{children}</form>
      </FormContext.Provider>
    </FormProvider>
  );
};
